import React, { useMemo } from 'react';
import { FaDrawPolygon, FaEye, FaMap, FaPen, FaTrash } from 'react-icons/fa';
import {
  clubAreaAcresLabel,
  clubAreaBoardGroups,
  clubAreaRoleLabel,
} from '../utils/clubAreaBoard';
import './ClubMapBoards.css';
import './MyGroundBoard.css';

function AreaRow({
  row,
  selected,
  onSelectArea,
  onEditArea,
  onEditAreaShape,
  onDeleteArea,
}) {
  const select = () => {
    if (onSelectArea) onSelectArea(row);
  };

  return (
    <div
      role="button"
      tabIndex={0}
      aria-current={selected ? 'true' : undefined}
      className={`feature-board-row${selected ? ' feature-board-row--selected' : ''}`}
      data-testid={`club-area-row-${row.id}`}
      onClick={select}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          select();
        }
      }}
    >
      <span className="feature-board-row__badge" aria-hidden="true">
        <FaDrawPolygon />
      </span>
      <span className="feature-board-row__body">
        <span className="feature-board-row__name">{row.name || 'Unnamed'}</span>
        <span className="feature-board-row__meta">{clubAreaRoleLabel(row)}</span>
      </span>
      <span className="feature-board-row__chip feature-board-row__chip--area">
        {clubAreaAcresLabel(row)}
      </span>
      <span
        className="feature-board-row__acts"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="feature-board-row__act"
          data-testid={`club-area-focus-${row.id}`}
          title="View on map"
          aria-label="View on map"
          onClick={() => onSelectArea && onSelectArea(row)}
        >
          <FaEye aria-hidden="true" />
        </button>
        {onEditAreaShape ? (
          <button
            type="button"
            className="feature-board-row__act"
            data-testid={`club-area-shape-${row.id}`}
            title="Edit shape"
            aria-label="Edit shape"
            onClick={() => onEditAreaShape(row)}
          >
            <FaMap aria-hidden="true" />
          </button>
        ) : null}
        {onEditArea ? (
          <button
            type="button"
            className="feature-board-row__act feature-board-row__act--primary"
            data-testid={`club-area-edit-${row.id}`}
            title="Edit details"
            aria-label="Edit details"
            onClick={() => onEditArea(row)}
          >
            <FaPen aria-hidden="true" />
          </button>
        ) : null}
        {onDeleteArea ? (
          <button
            type="button"
            className="feature-board-row__act feature-board-row__act--danger"
            data-testid={`club-area-delete-${row.id}`}
            title="Delete"
            aria-label="Delete"
            onClick={() => onDeleteArea(row)}
          >
            <FaTrash aria-hidden="true" />
          </button>
        ) : null}
      </span>
    </div>
  );
}

function AreaGroup({
  title,
  testId,
  rows,
  empty,
  selectedAreaId,
  onSelectArea,
  onEditArea,
  onEditAreaShape,
  onDeleteArea,
}) {
  return (
    <div className="club-area-board__group" data-testid={testId}>
      <h3 className="club-area-board__group-title">{title}</h3>
      {rows.length === 0 ? (
        <p className="club-area-board__empty">{empty}</p>
      ) : (
        <div className="club-area-board__list">
          {rows.map((row) => (
            <AreaRow
              key={row.id}
              row={row}
              selected={selectedAreaId != null && Number(selectedAreaId) === Number(row.id)}
              onSelectArea={onSelectArea}
              onEditArea={onEditArea}
              onEditAreaShape={onEditAreaShape}
              onDeleteArea={onDeleteArea}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function ClubAreaBoard({
  boundaries = [],
  selectedAreaId = null,
  onSelectArea,
  onEditArea,
  onEditAreaShape,
  onDeleteArea,
  onEditView,
}) {
  const groups = useMemo(() => clubAreaBoardGroups(boundaries), [boundaries]);
  const total = groups.tracts.length + groups.fields.length;

  return (
    <section className="club-area-board" data-testid="club-area-board" aria-label="Club areas">
      <header className="club-area-board__header">
        <span className="club-area-board__title">AREAS</span>
        <span className="club-area-board__header-acts">
          {onEditView ? (
            <button
              type="button"
              className="club-area-board__view-btn"
              data-testid="club-area-edit-view"
              onClick={onEditView}
            >
              SET VIEW
            </button>
          ) : null}
          <span className="club-area-board__count" data-testid="club-area-count">
            {total} {total === 1 ? 'POLYGON' : 'POLYGONS'}
          </span>
        </span>
      </header>
      <div className="club-area-board__groups">
        <AreaGroup
          title="Boundary"
          testId="club-area-tracts"
          rows={groups.tracts}
          empty="No club boundary yet."
          selectedAreaId={selectedAreaId}
          onSelectArea={onSelectArea}
          onEditArea={onEditArea}
          onEditAreaShape={onEditAreaShape}
          onDeleteArea={onDeleteArea}
        />
        <AreaGroup
          title="Fields"
          testId="club-area-fields"
          rows={groups.fields}
          empty="No fields yet."
          selectedAreaId={selectedAreaId}
          onSelectArea={onSelectArea}
          onEditArea={onEditArea}
          onEditAreaShape={onEditAreaShape}
          onDeleteArea={onDeleteArea}
        />
      </div>
    </section>
  );
}

export default ClubAreaBoard;
